//Step3Calculations.jsx
import React, { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LabelList,
  Brush,
  ReferenceLine
} from 'recharts';

// property formatting helpers
import { formatProperty } from '../utils/okobaudatProperties';

function formatImpactValue(value) {
  if (Number.isNaN(value) || value == null) return '—';
  const n = Number(value);
  const abs = Math.abs(n);
  if (abs >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (abs >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (abs === 0) return '0';
  if (abs < 0.001) return n.toExponential(2);
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

// ---- LCA stage controls ----
const LCA_STAGES = [
  'A1-A3','A1','A2','A3',
  'A4','A5',
  'B1','B2','B3','B4','B5','B6','B7',
  'C1','C2','C3','C4',
  'D'
];

export const DEFAULT_SELECTED = new Set(['A1-A3','C3','C4','D']);

const STAGE_ORDER = [
  'A1-A3','A1','A2','A3',
  'A4','A5',
  'B1','B2','B3','B4','B5','B6','B7',
  'C1','C2','C3','C4',
  'D'
];

const stageWeight = (s) => {
  const i = STAGE_ORDER.findIndex((x) => x.toLowerCase() === String(s).toLowerCase());
  return i === -1 ? 999 : i;
};

const norm = (k) => String(k ?? '').trim().toUpperCase();

function toModulesMap(mods) {
  if (!mods) return {};
  if (Array.isArray(mods)) {
    return Object.fromEntries(
      mods.map((m) => [norm(m.key ?? m.module ?? m.name ?? ''), Number(m.value ?? m.total ?? 0)])
    );
  }
  return Object.fromEntries(
    Object.entries(mods).map(([k, v]) => [norm(k), Number(v)])
  );
}

function pickModuleValue(mods, key) {
  const M = toModulesMap(mods);
  const K = norm(key);
  if (K === 'A1-A3') {
    // Prefer combined "A1-A3"; else sum A1+A2+A3
    if (Number.isFinite(M['A1-A3'])) return M['A1-A3'];
    return (M['A1'] || 0) + (M['A2'] || 0) + (M['A3'] || 0);
  }
  return M[K] || 0;
}

function sumSelectedModules(mods, selectedSet) {
  if (!mods || !selectedSet || selectedSet.size === 0) return 0;
  let total = 0;
  selectedSet.forEach((stage) => {
    total += pickModuleValue(mods, stage);
  });
  return total;
}

function getMergedModules(dataset) {
  if (!dataset) return null;
  if (!dataset.modules && !dataset.gwp) return null;
  const merged = {
    'A1-A3': dataset.gwp?.A1A3,
    A4: dataset.gwp?.A4,
    A5: dataset.gwp?.A5,
    B4: dataset.gwp?.B4,
    'C1-C4': dataset.gwp?.C1C4,
    D: dataset.gwp?.D,
    ...(dataset.modules || {})
  };
  return merged;
}

function getDeclaredFromSelectedStages(dataset, selectedStages) {
  if (!dataset) return null;
  const modules = getMergedModules(dataset);
  if (modules && selectedStages && selectedStages.size > 0) {
    const summed = sumSelectedModules(modules, selectedStages);
    if (Number.isFinite(summed)) return summed;
  }
  const fallbackTotal = Number(dataset?.gwpTotal);
  return Number.isFinite(fallbackTotal) ? fallbackTotal : null;
}

const ModulesGrid = ({ modules }) => {
  if (!modules || (Array.isArray(modules) && modules.length === 0)) return null;

  // Accept either a plain object {A1:..., A2:...} or an array of {key,value}
  let entries = [];
  if (Array.isArray(modules)) {
    entries = modules.map((m) => [m.key ?? m.module ?? m.name ?? '', m.value ?? m.total ?? 0]);
  } else {
    entries = Object.entries(modules);
  }

  // Keep a friendly sort (A1..A3, A4..A5, B1..B7, C1..C4, D)
  const order = [
    'A1','A2','A3','A1-A3',
    'A4','A5',
    'B1','B2','B3','B4','B5','B6','B7',
    'C1','C2','C3','C4',
    'D'
  ];
  const weight = (k) => {
    const i = order.findIndex((x) => x.toLowerCase() === String(k).toLowerCase());
    return i === -1 ? 999 : i;
  };

  const sorted = entries
    .filter(([k]) => String(k).trim().length > 0)
    .sort((a, b) => weight(a[0]) - weight(b[0]));

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
      {sorted.map(([k, v]) => (
        <div key={k} className="rounded border border-emerald-100 px-2 py-1 text-xs flex items-center justify-between bg-white">
          <span className="font-medium text-emerald-900">{k}</span>
          <span className="text-emerald-900">{formatImpactValue(v)}</span>
        </div>
      ))}
    </div>
  );
};

function getPropsMap(materialProperties) {
  if (!materialProperties) return null;
  if (materialProperties.properties && typeof materialProperties.properties === 'object') {
    return materialProperties.properties;
  }
  if (typeof materialProperties === 'object') return materialProperties;
  return null;
}

function pickProperty(propsMap, keys) {
  if (!propsMap || typeof propsMap !== 'object') return null;
  const entries = Object.entries(propsMap);
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase() === target.toLowerCase());
    if (found) return { key: found[0], value: found[1] };
  }
  for (const target of keys) {
    const found = entries.find(([k]) => k.toLowerCase().includes(target.toLowerCase()));
    if (found) return { key: found[0], value: found[1] };
  }
  return null;
}

function renderPropValue(entry) {
  if (entry == null) return '—';
  if (typeof entry === 'number') return entry.toLocaleString();
  if (typeof entry === 'string') return entry;
  if (typeof entry === 'object') return formatProperty(entry);
  return String(entry);
}

// --- Conversion helpers ---

// --- Chart helpers ---
const ModernTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value;
  const extra = payload[0]?.payload?.__tooltipMeta;
  return (
    <div className="rounded-lg border border-emerald-100 bg-white/95 shadow-sm px-3 py-2">
      <div className="text-sm text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-emerald-900">
        {typeof val === 'number' ? formatImpactValue(val) : val}
      </div>
      {extra?.best != null && typeof val === 'number' ? (
        <div className="mt-1 text-xs text-gray-500">
          {val === extra.best
            ? 'Best (lowest) value'
            : `+${formatImpactValue(val - extra.best)} vs. best`}
        </div>
      ) : null}
    </div>
  );
};

const DetailedTooltip = ({ active, payload, label, hoveredKey }) => {
  if (!active || !payload || !payload.length) return null;
  const target = hoveredKey
    ? payload.find((p) => p.dataKey === hoveredKey) || payload[0]
    : payload[0];
  const val = target?.value;
  const extra = target?.payload?.__tooltipMeta;

  return (
    <div className="rounded-lg border border-emerald-100 bg-white/95 shadow-sm px-3 py-2">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-sm font-semibold text-emerald-900">
        {typeof val === 'number' ? formatImpactValue(val) : val}
      </div>
      {extra?.best != null && typeof val === 'number' ? (
        <div className="mt-1 text-xs text-gray-500">
          {val === extra.best ? 'Best (lowest) value' : `+${formatImpactValue(val - extra.best)} vs. best`}
        </div>
      ) : null}
    </div>
  );
};

function getChartWidthPx(barCount) {
  const MIN_WIDTH = 420;
  const PER_BAR = 160;
  const SIDE_PAD = 80;
  return Math.max(MIN_WIDTH, barCount * PER_BAR + SIDE_PAD);
}

function getBarSizePx(containerWidth, barCount) {
  if (!barCount || containerWidth <= 0) return 48;
  const usable = Math.max(0, containerWidth - 96);
  const perBar = usable / barCount;
  return Math.max(36, Math.min(72, Math.floor(perBar * 0.6)));
}

const BAR_COLOR = '#10b981';
const BAR_ACTIVE = '#059669';
const BAR_INACTIVE = '#34d399';
const WALL_COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#a3e635'];

const GENERIC_DENSITY_KG_PER_M3 = [
  { key: 'concrete', density: 2400 },
  { key: 'reinforced concrete', density: 2500 },
  { key: 'cement', density: 1440 },
  { key: 'steel', density: 7850 },
  { key: 'aluminum', density: 2700 },
  { key: 'copper', density: 8960 },
  { key: 'glass', density: 2500 },
  { key: 'brick', density: 1800 },
  { key: 'masonry', density: 2000 },
  { key: 'stone', density: 2600 },
  { key: 'timber', density: 600 },
  { key: 'wood', density: 600 },
  { key: 'gypsum', density: 850 },
  { key: 'insulation', density: 30 },
  { key: 'asphalt', density: 2300 },
  { key: 'plastic', density: 950 }
];

const GENERIC_AREAL_DENSITY_KG_PER_M2 = [
  { key: 'gypsum board', density: 10 },
  { key: 'drywall', density: 10 },
  { key: 'plywood', density: 7 },
  { key: 'osb', density: 12 },
  { key: 'glass', density: 25 },
  { key: 'insulation', density: 3 },
  { key: 'roofing membrane', density: 5 },
  { key: 'carpet', density: 2.5 },
  { key: 'ceramic tile', density: 20 },
  { key: 'vinyl', density: 3 },
  { key: 'cladding', density: 18 },
  { key: 'flooring', density: 15 },
  { key: 'plaster', density: 12 },
  { key: 'acoustic panel', density: 8 },
  { key: 'metal sheet', density: 12 }
];

function getGenericDensityFromDataset(dataset) {
  const name = `${dataset?.name || ''} ${dataset?.subcategory || ''}`.toLowerCase();
  const match = GENERIC_DENSITY_KG_PER_M3.find((entry) => name.includes(entry.key));
  return match ? match.density : null;
}

function getGenericArealFromDataset(dataset) {
  const name = `${dataset?.name || ''} ${dataset?.subcategory || ''}`.toLowerCase();
  const match = GENERIC_AREAL_DENSITY_KG_PER_M2.find((entry) => name.includes(entry.key));
  return match ? match.density : null;
}

function normalizeUnit(raw) {
  if (!raw) return null;
  const u = String(raw).trim().toLowerCase().replace(/\s+/g, '');
  if (/(m\^?2|m²)/.test(u)) return 'm2';
  if (/(m\^?3|m³)/.test(u)) return 'm3';
  if (/^kg$/.test(u)) return 'kg';
  if (/^(t|ton|tonne|tonnes|tons|1000kg|1t)$/.test(u)) return 't';
  if (/^g$/.test(u)) return 'g';
  if (/piece|unit|ea|each|m$/.test(u)) return 'unsupported';
  return u;
}

function getDeclaredUnitInfo(dataset) {
  const du = dataset?.declaredUnit || {};
  const amount = Number(du.amount ?? 1) || 1;
  const unit = normalizeUnit(du.unit);
  return { amount, unit };
}

function extractProps(dataset) {
  const propsMap = getPropsMap(dataset?.materialProperties) || {};
  const densityEntry = pickProperty(propsMap, ['bulkDensity', 'grossDensity', 'density']);
  let grammageEntry = pickProperty(propsMap, ['surfaceWeight']);
  if (!grammageEntry) {
    grammageEntry = pickProperty(propsMap, ['massPerAreaKgPerM2']);
  }
  if (!grammageEntry) {
    const entries = Object.entries(propsMap);
    const guess = entries.find(([k]) => {
      const kl = k.toLowerCase();
      return (
        kl.includes('surfaceweight') ||
        (kl.includes('surface') && kl.includes('weight')) ||
        kl.includes('areal') ||
        kl.includes('areic') ||
        kl.includes('areadensity') ||
        kl.includes('areal density') ||
        kl.includes('areal_density') ||
        (kl.includes('fl') && kl.includes('chengewicht'))
      );
    });
    if (guess) grammageEntry = { key: guess[0], value: guess[1] };
  }

  const toNum = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^0-9eE.+-]/g, ''));
      return Number.isFinite(n) ? n : null;
    }
    if (typeof v === 'object') {
      const numeric = Number(v.value ?? v.amount ?? v.number);
      return Number.isFinite(numeric) ? numeric : null;
    }
    return null;
  };

  const densityFromProps = densityEntry ? toNum(densityEntry.value) : null;
  const grammageFromProps = grammageEntry ? toNum(grammageEntry.value) : null;
  const genericDensity = getGenericDensityFromDataset(dataset);
  const genericAreal = getGenericArealFromDataset(dataset);
  const density = densityFromProps ?? genericDensity;
  const grammage = grammageFromProps ?? genericAreal;
  return {
    density,
    grammage,
    usedGenericDensity: densityFromProps == null && Number.isFinite(genericDensity),
    usedGenericGrammage: grammageFromProps == null && Number.isFinite(genericAreal)
  };
}

function computeConversionPerM2({ dataset, thicknessCm, selectedStages }) {
  const { amount, unit } = getDeclaredUnitInfo(dataset);
  if (!unit) return { ok: false, reason: 'No declared unit', expl: 'Missing declared unit' };

  const declaredForStages = getDeclaredFromSelectedStages(dataset, selectedStages);
  if (!Number.isFinite(declaredForStages)) {
    return { ok: false, reason: 'No GWP', expl: 'Missing numeric GWP for selected stages' };
  }

  const base = declaredForStages / (Number(amount) || 1);
  const thickness_m = Number.isFinite(thicknessCm) ? thicknessCm / 100 : null;
  const { density, grammage, usedGenericDensity, usedGenericGrammage } = extractProps(dataset);
  const fmt = (val, digits = 3) => (Number.isFinite(val) ? val.toFixed(digits) : 'n/a');
  const genericNote = [
    usedGenericDensity ? 'Using generic density.' : null,
    usedGenericGrammage ? 'Using generic areal density.' : null
  ]
    .filter(Boolean)
    .join(' ');
  const withNote = (text) => (genericNote ? `${text} ${genericNote}` : text);

  switch (unit) {
    case 'm2':
      return {
        ok: false,
        reason: 'Already per m²',
        expl: withNote('Declared per m² — no conversion')
      };

    case 'm3': {
      if (!Number.isFinite(thickness_m) || thickness_m <= 0) {
        return { ok: false, reason: 'Needs thickness', expl: 'm³ → m² needs layer thickness' };
      }
      const perM2 = base * thickness_m;
      const expl = `m³ → m²: thickness ${fmt(thickness_m)} m; converted = ${formatImpactValue(base)} × ${fmt(thickness_m)}`;
      return { ok: true, perM2, expl: withNote(expl) };
    }

    case 'kg': {
      let massPerM2 = null;
      if (Number.isFinite(grammage) && grammage > 0) {
        massPerM2 = grammage;
      } else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) {
        massPerM2 = density * thickness_m;
      } else {
        return { ok: false, reason: 'Needs density or grammage', expl: 'kg → m² needs grammage or density × thickness' };
      }
      const perM2 = base * massPerM2;
      const expl = Number.isFinite(grammage)
        ? `kg → m²: grammage ${fmt(grammage)} kg/m²; converted = ${formatImpactValue(base)} × ${fmt(grammage)}`
        : `kg → m²: density ${fmt(density)} × thickness ${fmt(thickness_m)} = ${fmt(massPerM2)} kg/m²; converted = ${formatImpactValue(base)} × ${fmt(massPerM2)}`;
      return { ok: true, perM2, expl: withNote(expl) };
    }

    case 't': {
      let massPerM2 = null;
      if (Number.isFinite(grammage) && grammage > 0) {
        massPerM2 = grammage;
      } else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) {
        massPerM2 = density * thickness_m;
      } else {
        return { ok: false, reason: 'Needs density or grammage', expl: 't → m² needs grammage or density × thickness' };
      }
      const perM2 = base * (massPerM2 / 1000);
      const expl = Number.isFinite(grammage)
        ? `t → m²: grammage ${fmt(grammage)} kg/m² = ${fmt(grammage / 1000)} t/m²; converted = ${formatImpactValue(base)} × ${fmt(grammage / 1000)}`
        : `t → m²: ${fmt(massPerM2)} kg/m² = ${fmt(massPerM2 / 1000)} t/m²; converted = ${formatImpactValue(base)} × ${fmt(massPerM2 / 1000)}`;
      return { ok: true, perM2, expl: withNote(expl) };
    }

    case 'g': {
      let massPerM2kg = null;
      if (Number.isFinite(grammage) && grammage > 0) {
        massPerM2kg = grammage;
      } else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) {
        massPerM2kg = density * thickness_m;
      } else {
        return { ok: false, reason: 'Needs density or grammage', expl: 'g → m² needs grammage or density × thickness' };
      }
      const grams = massPerM2kg * 1000;
      const perM2 = base * grams;
      const expl = `g → m²: ${fmt(grams, 1)} g/m²; converted = ${formatImpactValue(base)} × ${fmt(grams, 1)}`;
      return { ok: true, perM2, expl: withNote(expl) };
    }

    default:
      return {
        ok: false,
        reason: 'Unsupported unit',
        expl: withNote(`Unsupported declared unit "${dataset?.declaredUnit?.unit ?? 'unknown'}"`)
      };
  }
}
function getDeclaredForStage(dataset, stageKey) {
  if (!dataset) return null;
  const modules = getMergedModules(dataset);
  if (!modules) return null;
  const v = pickModuleValue(modules, stageKey);
  return Number.isFinite(v) ? v : null;
}

function computeConversionPerM2ForStage({ dataset, thicknessCm, stageKey }) {
  const { amount, unit } = getDeclaredUnitInfo(dataset);
  if (!unit) return { ok: false, reason: 'No declared unit', expl: 'Missing declared unit' };

  const declaredForStage = getDeclaredForStage(dataset, stageKey);
  if (!Number.isFinite(declaredForStage)) {
    return { ok: false, reason: 'No GWP (stage)', expl: `Missing GWP for ${stageKey}` };
  }

  const base = declaredForStage / (Number(amount) || 1);
  const thickness_m = Number.isFinite(thicknessCm) ? thicknessCm / 100 : null;
  const { density, grammage, usedGenericDensity, usedGenericGrammage } = extractProps(dataset);
  const fmt = (val, digits = 3) => (Number.isFinite(val) ? val.toFixed(digits) : 'n/a');
  const genericNote = [
    usedGenericDensity ? 'Using generic density.' : null,
    usedGenericGrammage ? 'Using generic areal density.' : null
  ]
    .filter(Boolean)
    .join(' ');
  const withNote = (text) => (genericNote ? `${text} ${genericNote}` : text);

  switch (unit) {
    case 'm2':
      return {
        ok: false,
        reason: 'Already per m²',
        expl: withNote('Declared per m² — no conversion')
      };
    case 'm3': {
      if (!Number.isFinite(thickness_m) || thickness_m <= 0) {
        return { ok: false, reason: 'Needs thickness', expl: 'm³ → m² needs layer thickness' };
      }
      return { ok: true, perM2: base * thickness_m, expl: withNote(`m³ → m² × ${fmt(thickness_m)}`) };
    }
    case 'kg': {
      let massPerM2 = null;
      if (Number.isFinite(grammage) && grammage > 0) massPerM2 = grammage;
      else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) massPerM2 = density * thickness_m;
      else return { ok: false, reason: 'Needs density or grammage', expl: 'kg → m² needs grammage or density × thickness' };
      return { ok: true, perM2: base * massPerM2, expl: withNote(`kg → m² × ${fmt(massPerM2)}`) };
    }
    case 't': {
      let massPerM2 = null;
      if (Number.isFinite(grammage) && grammage > 0) massPerM2 = grammage;
      else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) massPerM2 = density * thickness_m;
      else return { ok: false, reason: 'Needs density or grammage', expl: 't → m² needs grammage or density × thickness' };
      return { ok: true, perM2: base * (massPerM2 / 1000), expl: withNote(`t → m² × ${fmt(massPerM2 / 1000)}`) };
    }
    case 'g': {
      let massPerM2kg = null;
      if (Number.isFinite(grammage) && grammage > 0) massPerM2kg = grammage;
      else if (Number.isFinite(density) && Number.isFinite(thickness_m) && thickness_m > 0) massPerM2kg = density * thickness_m;
      else return { ok: false, reason: 'Needs density or grammage', expl: 'g → m² needs grammage or density × thickness' };
      const grams = massPerM2kg * 1000;
      return { ok: true, perM2: base * grams, expl: withNote(`g → m² × ${fmt(grams, 1)}`) };
    }
    default:
      return {
        ok: false,
        reason: 'Unsupported unit',
        expl: withNote(`Unsupported unit "${dataset?.declaredUnit?.unit ?? 'unknown'}"`)
      };
  }
}
const resolveSelectedStages = (value) => {
  if (value instanceof Set) return value;
  if (Array.isArray(value)) return new Set(value);
  return null;
};

export const buildWallRows = ({ wall, assignments, shortlistByUuid, selectedStages, adjustments }) => {
  const selectedSet = selectedStages || new Set(DEFAULT_SELECTED);
  const rows = wall.layers.map((layer) => {
    const chosenUuid = (assignments?.[wall.id] || {})[layer.id] || '';
    const dataset = shortlistByUuid.get(chosenUuid);
    const unitNorm = normalizeUnit(dataset?.declaredUnit?.unit);
    const conversion = dataset
      ? computeConversionPerM2({ dataset, thicknessCm: layer.thicknessCm, selectedStages: selectedSet })
      : null;
    const declaredStageFiltered = dataset ? getDeclaredFromSelectedStages(dataset, selectedSet) : null;

    let baseValue = null;
    let conversionUsed = false;
    let conversionExpl = '';

    if (dataset) {
      if (unitNorm !== 'm2' && conversion?.ok) {
        const numeric = Number(conversion?.perM2);
        baseValue = Number.isFinite(numeric) ? numeric : null;
        conversionUsed = true;
        conversionExpl = conversion?.expl || '';
      } else {
        baseValue = Number.isFinite(declaredStageFiltered) ? declaredStageFiltered : null;
        conversionExpl = conversion?.expl || '';
      }
    }

    const rawAdjustment = (adjustments?.[wall.id] || {})[layer.id];
    const adjustment = Number.isFinite(rawAdjustment) ? rawAdjustment : 1;
    const perLayerGwp = Number.isFinite(baseValue) ? baseValue * adjustment : null;

    return {
      layer,
      chosenUuid,
      dataset,
      unitNorm,
      conversion,
      conversionUsed,
      conversionExpl,
      baseValue,
      adjustment,
      perLayerGwp,
    };
  });

  const total = rows.reduce((acc, r) => acc + (Number(r.perLayerGwp) || 0), 0);
  const hasCalculatedLayer = rows.some((r) => Number.isFinite(r.perLayerGwp));
  return { rows, total, hasCalculatedLayer };
};

export const ShortlistTable = ({ shortlistedDatasets, onRemoveShortlist, onEditShortlist }) => {
  const [expanded, setExpanded] = useState({});
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [pendingRemoveItem, setPendingRemoveItem] = useState(null);
  const CUSTOM_EPD_LIBRARY_KEY = 'embodix_custom_epds_v1';
  const LCA_STORAGE_KEY = 'embodix_lca_mvp_state_v1';

  const removeFromCustomLibrary = (uuid) => {
    try {
      const raw = localStorage.getItem(CUSTOM_EPD_LIBRARY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) return;
      const next = parsed.filter((item) => item?.uuid !== uuid);
      localStorage.setItem(CUSTOM_EPD_LIBRARY_KEY, JSON.stringify(next));
    } catch (err) {
      console.error('Failed to update custom EPD library', err);
    }
  };

  const removeFromLcaLibrary = (uuid) => {
    try {
      const raw = localStorage.getItem(LCA_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || !Array.isArray(parsed.epdLibrary)) return;
      const next = parsed.epdLibrary.filter((item) => item?.id !== uuid);
      localStorage.setItem(LCA_STORAGE_KEY, JSON.stringify({ ...parsed, epdLibrary: next }));
    } catch (err) {
      console.error('Failed to update LCA library storage', err);
    }
  };

  const isCustomEpd = (item) => item?.source === 'custom' || item?.subtype === 'custom';
  const requestRemove = (item) => {
    if (!item?.uuid) return;
    if (!isCustomEpd(item)) {
      onRemoveShortlist?.(item.uuid);
      return;
    }
    setPendingRemoveItem(item);
    setRemoveDialogOpen(true);
  };

  const confirmRemove = () => {
    if (!pendingRemoveItem?.uuid) return;
    removeFromCustomLibrary(pendingRemoveItem.uuid);
    removeFromLcaLibrary(pendingRemoveItem.uuid);
    onRemoveShortlist?.(pendingRemoveItem.uuid);
    setPendingRemoveItem(null);
    setRemoveDialogOpen(false);
  };

  return (
    <section className="space-y-3">
      <div>
        <h3 className="text-base font-semibold text-emerald-900">Shortlisted datasets</h3>
        <p className="text-sm text-gray-600">
          These are the materials you shortlisted in Step 1. Expand any row to see life-cycle stages and material properties.
        </p>
      </div>

      <div className="rounded-lg border border-emerald-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-emerald-50 text-emerald-900">
              <tr>
                <th className="text-left px-3 py-2 font-medium w-12">#</th>
                <th className="text-left px-3 py-2 font-medium">Dataset</th>
                <th className="text-left px-3 py-2 font-medium">Declared unit</th>
                <th className="text-left px-3 py-2 font-medium">Total GWP (declared)</th>
                <th className="text-left px-3 py-2 font-medium w-52">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(shortlistedDatasets || []).length === 0 ? (
                <tr className="bg-white">
                  <td colSpan={5} className="px-3 py-3 text-sm text-gray-400">
                    Nothing shortlisted yet. Shortlist from the database or create your own EPD.
                  </td>
                </tr>
              ) : (
                shortlistedDatasets.map((item, idx) => {
                  const du = item?.declaredUnit;
                  const duText = du ? `${du.amount ?? 1} ${du.unit || ''}`.trim() : '—';
                  const isOpen = !!expanded[item.uuid];

                  return (
                    <React.Fragment key={item.uuid}>
                      <tr className="odd:bg-white even:bg-emerald-50/40">
                        <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                        <td className="px-3 py-2 text-emerald-900">{item.name}</td>
                        <td className="px-3 py-2 text-emerald-900">{duText}</td>
                        <td className="px-3 py-2 text-emerald-900">{formatImpactValue(item.gwpTotal)}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-start gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 border-emerald-200 text-emerald-900 bg-white hover:bg-emerald-50"
                              onClick={() => setExpanded((prev) => ({ ...prev, [item.uuid]: !isOpen }))}
                            >
                              {isOpen ? 'Hide details' : 'Details'}
                            </Button>
                            {(item.subtype === 'custom' || item.source === 'custom') && (
                              <Button
                                type="button"
                                variant="outline"
                                className="h-7 px-2 border-emerald-200 text-emerald-900 bg-white hover:bg-emerald-50"
                                onClick={() => onEditShortlist?.(item)}
                              >
                                Edit
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              className="h-7 px-2 border-red-300 text-red-700 bg-white hover:bg-red-50"
                              onClick={() => requestRemove(item)}
                            >
                              Remove
                            </Button>
                          </div>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-white">
                          <td colSpan={5} className="px-3 py-3">
                            <div className="rounded-lg border border-emerald-100 p-3 bg-emerald-50/40">
                              <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                  <h4 className="text-xs font-semibold text-emerald-900 uppercase tracking-wide mb-2">
                                    Life-cycle stages
                                  </h4>
                                  <ModulesGrid modules={item.modules} />
                                </div>
                                <div>
                                  <h4 className="text-xs font-semibold text-emerald-900 uppercase tracking-wide mb-2">
                                    Material properties
                                  </h4>
                                  <div className="space-y-2 text-sm">
                                    {(() => {
                                      const propsMap = getPropsMap(item.materialProperties);
                                      if (!propsMap || Object.keys(propsMap).length === 0) {
                                        return <div className="text-gray-600">No material properties were provided by this dataset.</div>;
                                      }

                                      const density = pickProperty(propsMap, ['bulkDensity', 'grossDensity', 'density']);
                                      const thickness = pickProperty(propsMap, ['layerThickness', 'thickness']);

                                      let grammage = pickProperty(propsMap, ['surfaceWeight']);
                                      if (!grammage) {
                                        const entries = Object.entries(propsMap);
                                        const guess = entries.find(([k]) => {
                                          const kl = k.toLowerCase();
                                          return (
                                            kl.includes('surfaceweight') ||
                                            (kl.includes('surface') && kl.includes('weight')) ||
                                            kl.includes('areal') ||
                                            kl.includes('areic') ||
                                            kl.includes('areadensity') ||
                                            kl.includes('areal density') ||
                                            kl.includes('areal_density') ||
                                            (kl.includes('fl') && kl.includes('chengewicht'))
                                          );
                                        });
                                        if (guess) grammage = { key: guess[0], value: guess[1] };
                                      }

                                      const rows = [
                                        density && {
                                          label: (typeof density.value?.label === 'string' && density.value.label.trim()) ? density.value.label : 'Density',
                                          value: density.value,
                                        },
                                        thickness && {
                                          label: (typeof thickness.value?.label === 'string' && thickness.value.label.trim()) ? thickness.value.label : 'Thickness',
                                          value: thickness.value,
                                        },
                                        grammage && {
                                          label: (typeof grammage.value?.label === 'string' && grammage.value.label.trim()) ? grammage.value.label : 'Grammage',
                                          value: grammage.value,
                                        },
                                      ].filter(Boolean);

                                      if (rows.length === 0) {
                                        return <div className="text-gray-600">No material properties were provided by this dataset.</div>;
                                      }

                                      return (
                                        <>
                                          {rows.map((row, i) => (
                                            <div
                                              key={i}
                                              className="flex items-center justify-between rounded border border-emerald-100 bg-white px-2 py-1"
                                            >
                                              <span className="text-emerald-900">{row.label}</span>
                                              <span className="text-emerald-900">{renderPropValue(row.value)}</span>
                                            </div>
                                          ))}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Remove custom EPD?</DialogTitle>
            <DialogDescription className="text-gray-700">
              This will remove the custom EPD from your shortlist and library. This action cannot be reverted.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" className="text-gray-900" onClick={() => setRemoveDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" className="bg-red-600 text-white hover:bg-red-700" onClick={confirmRemove}>
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export const WallAssignmentsTable = ({
  wall,
  shortlistedDatasets,
  assignments,
  onAssign,
  adjustments,
  onAdjustmentChange,
  selectedStages,
}) => {
  const shortlistByUuid = useMemo(() => {
    const m = new Map();
    (shortlistedDatasets || []).forEach((d) => m.set(d.uuid, d));
    return m;
  }, [shortlistedDatasets]);

  const selected = useMemo(() => resolveSelectedStages(selectedStages) || new Set(DEFAULT_SELECTED), [selectedStages]);
  const { rows, total } = useMemo(
    () => buildWallRows({ wall, assignments, shortlistByUuid, selectedStages: selected, adjustments }),
    [wall, assignments, shortlistByUuid, selected, adjustments]
  );

  const GRID_COLS = 'grid grid-cols-[48px_minmax(0,1.2fr)_88px_minmax(0,2.1fr)_152px_124px_120px_120px] items-center';
  const GRID_HEADER = `${GRID_COLS} px-2 py-1 bg-emerald-50/80 text-emerald-900 text-xs font-semibold`;
  const GRID_ROW = `${GRID_COLS} px-2 py-1 text-xs text-emerald-900 even:bg-emerald-50/30`;
  const NUM = 'text-right tabular-nums';

  return (
    <div className="space-y-1">
      <div className="text-sm font-semibold text-emerald-900 mb-3 mt-3 text-center">Layer assignments & totals</div>

      <div className="overflow-x-auto">
        <div className="min-w-[840px]">
          <div className={GRID_HEADER}>
            <span>#</span>
            <span>Layer name</span>
            <span>Thick (cm)</span>
            <span>Assigned EPD</span>
            <span className="pr-2 text-center">Conversion</span>
            <span className="text-center pr-2">GWP/m²</span>
            <span className="text-center pr-2">Adjustment</span>
            <span className="text-right pr-2">Total GWP</span>
          </div>

          <ScrollArea className="max-h-[360px]">
            <div className="divide-y divide-emerald-100 bg-white">
              {rows.map((row, idx) => {
                const { layer, chosenUuid, dataset, unitNorm, conversion, conversionUsed, conversionExpl, baseValue, adjustment, perLayerGwp } = row;
                const thicknessDisplay = Number.isFinite(layer.thicknessCm)
                  ? Number(layer.thicknessCm).toLocaleString(undefined, { maximumFractionDigits: 2 })
                  : '—';

                const convertedDisplay = dataset && Number.isFinite(baseValue) ? formatImpactValue(baseValue) : '—';
                const totalDisplay = dataset && Number.isFinite(perLayerGwp) ? perLayerGwp.toFixed(2) : '-';
                const conversionLabel = !dataset
                  ? 'Select a dataset'
                  : unitNorm === 'm2' || conversionUsed
                  ? 'Done'
                  : conversion?.reason
                  ? 'Error'
                  : 'Done';
                const conversionInfo = conversionExpl || (conversion?.reason
                  ? `Error: ${conversion.reason}. ${conversion?.expl || ''}`
                  : '');

                const adjustmentValue = Number.isFinite(adjustment) ? adjustment : 1;

                return (
                  <div key={layer.id} className={GRID_ROW}>
                    <div className="text-xs text-gray-500">{idx + 1}</div>
                    <div className="text-xs text-emerald-900 pr-2">{layer.name || `Layer ${idx + 1}`}</div>
                    <div className="text-xs text-emerald-900 pr-2 text-center sm:text-left">{thicknessDisplay}</div>
                    <div className="pr-2">
                      <select
                        value={chosenUuid}
                        onChange={(e) => onAssign?.(wall.id, layer.id, e.target.value)}
                        className="w-full border border-emerald-200 rounded px-2 py-0.5 text-xs bg-white text-emerald-900"
                        style={{ color: chosenUuid ? undefined : '#9ca3af' }}
                      >
                        <option value="" className="text-gray-400 text-xs">— Select from shortlist —</option>
                        {(shortlistedDatasets || []).map((opt, ix) => (
                          <option key={opt.uuid} value={opt.uuid} className="text-emerald-900">
                            {ix + 1}. {opt.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="pr-2 flex items-center justify-center gap-1 text-sm text-emerald-900">
                      <span className={`truncate text-xs ${!dataset ? "text-gray-400" : "text-emerald-900"}`}>{conversionLabel}</span>
                      {conversionInfo ? (
                        <span className="inline-flex items-center" title={conversionInfo}>
                          <Info className="w-3.5 h-3.5 text-emerald-700 shrink-0" />
                        </span>
                      ) : null}
                    </div>
                    <div className="text-sm text-emerald-900 pr-2 text-center">{convertedDisplay}</div>
                    <div className="pr-2 text-center">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={adjustmentValue}
                        onChange={(e) => onAdjustmentChange?.(wall.id, layer.id, Number(e.target.value))}
                        className="w-24 border border-emerald-200 rounded px-2 py-0.5 text-sm bg-white text-emerald-900 text-center"
                      />
                    </div>
                    <div className={`${NUM} text-xs text-emerald-900 pr-2 text-left`}>
                      {totalDisplay === '-' ? '-' : (
                        <span>{totalDisplay} kgCO2e/m<sup>2</sup></span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>

      <div className="flex items-center justify-end px-2 py-2 bg-emerald-50/70 border-t border-emerald-100">
        <div className="text-xs text-emerald-900 font-medium">
          Total: {Number.isFinite(total) ? <span className="font-bold">{total.toFixed(2)} kgCO2e/m<sup>2</sup></span> : '-'}
        </div>
      </div>
    </div>
  );
};

const Step3Calculations = ({
  shortlistedDatasets,
  walls,
  assignments,
  adjustments,
  selectedStages: controlledSelectedStages,
  onSelectedStagesChange,
  hideHeader = false,
  unstyled = false
}) => {
  const shortlistByUuid = useMemo(() => {
    const m = new Map();
    (shortlistedDatasets || []).forEach((d) => m.set(d.uuid, d));
    return m;
  }, [shortlistedDatasets]);

  const [internalStages, setInternalStages] = useState(new Set(DEFAULT_SELECTED));
  const selectedStages = useMemo(
    () => resolveSelectedStages(controlledSelectedStages) || internalStages,
    [controlledSelectedStages, internalStages]
  );

  const setSelectedStages = (next) => {
    if (onSelectedStagesChange) onSelectedStagesChange(next);
    if (!controlledSelectedStages) setInternalStages(next);
  };

  const [showDetails, setShowDetails] = useState(false);

  const calcWallTotals = (wall) =>
    buildWallRows({ wall, assignments, shortlistByUuid, selectedStages, adjustments });

  const chartData = useMemo(() => {
    if (!Array.isArray(walls)) return [];
    const rows = walls
      .map((wall, i) => {
        const { total, hasCalculatedLayer } = calcWallTotals(wall);
        return {
          name: wall.name || `Wall ${i + 1}`,
          total,
          hasCalculatedLayer
        };
      })
      .filter((row) => row.hasCalculatedLayer);
    if (!rows.length) return [];
    const best = Math.min(...rows.map((r) => Number(r.total) || 0));
    return rows.map(({ hasCalculatedLayer, ...rest }) => ({ ...rest, __tooltipMeta: { best } }));
  }, [walls, assignments, shortlistByUuid, selectedStages, adjustments]);

  const detailedData = useMemo(() => {
    if (!showDetails || !Array.isArray(walls) || !walls.length) return [];
    const stages = Array.from(selectedStages).sort((a, b) => stageWeight(a) - stageWeight(b));
    return stages.map((stage) => {
      const row = { stage };
      let best = null;
      walls.forEach((wall) => {
        const assignment = assignments?.[wall.id] || {};
        let sum = 0;
        wall.layers.forEach((layer) => {
          const uuid = assignment[layer.id] || '';
          const ds = shortlistByUuid.get(uuid);
          if (!ds) return;

          const unitNorm = normalizeUnit(ds?.declaredUnit?.unit);
          let base = null;
          if (unitNorm !== 'm2') {
            const conv = computeConversionPerM2ForStage({ dataset: ds, thicknessCm: layer.thicknessCm, stageKey: stage });
            base = conv.ok ? conv.perM2 : getDeclaredForStage(ds, stage);
          } else {
            base = getDeclaredForStage(ds, stage);
          }

          const adj = Number((adjustments?.[wall.id] || {})[layer.id]) || 1;
          if (Number.isFinite(base)) sum += base * adj;
        });
        const key = wall.name || wall.id || 'wall';
        row[key] = sum;
        best = best == null ? sum : Math.min(best, sum);
      });
      row.__tooltipMeta = { best };
      return row;
    });
  }, [showDetails, walls, selectedStages, shortlistByUuid, assignments, adjustments]);

  const chartWidthPx = useMemo(() => getChartWidthPx(chartData.length), [chartData.length]);
  const [activeBarIndex, setActiveBarIndex] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const barSizePx = useMemo(() => getBarSizePx(chartWidthPx, chartData.length), [chartWidthPx, chartData.length]);
  const detailedBarGroups = detailedData.length;
  const wallsCount = Array.isArray(walls) ? walls.length : 0;
  const detailedBarsTotal = detailedBarGroups * wallsCount;

  const detailedWidthPx = useMemo(() => {
    if (!showDetails) return chartWidthPx;
    const MIN = 520;
    const PER_BAR = 56;
    const SIDE = 96;
    return Math.max(MIN, detailedBarsTotal * PER_BAR + SIDE);
  }, [showDetails, detailedBarsTotal, chartWidthPx]);

  const detailedBarSize = useMemo(
    () => Math.max(28, Math.min(56, Math.floor((detailedWidthPx - 96) / Math.max(1, detailedBarsTotal)) * 0.76)),
    [detailedWidthPx, detailedBarsTotal]
  );

  const lowestImpact = useMemo(() => {
    if (!Array.isArray(walls) || walls.length === 0) return null;
    let best = null;
    walls.forEach((wall) => {
      const { total, hasCalculatedLayer } = calcWallTotals(wall);
      if (!hasCalculatedLayer) return;
      if (!best || total < best.total) best = { wall, total };
    });
    return best;
  }, [walls, assignments, shortlistByUuid, selectedStages, adjustments]);

  const titleText = 'Results';
  const subtitleText = 'Review lifecycle totals for your wall sections and compare across shortlisted materials.';

  const sections = (
    <div className="space-y-6">
      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" className="h-8 px-3 border-emerald-200 text-emerald-900 bg-white hover:bg-emerald-50">
                Choose LCA stages
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuLabel>Include in totals</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LCA_STAGES.map((stage) => {
                const checked = selectedStages.has(stage);
                return (
                  <DropdownMenuCheckboxItem
                    key={stage}
                    checked={checked}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(v) => {
                      const next = new Set(selectedStages);
                      if (v) next.add(stage);
                      else next.delete(stage);
                      setSelectedStages(next.size === 0 ? new Set(DEFAULT_SELECTED) : next);
                    }}
                  >
                    {stage}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-base font-semibold text-emerald-900">Results</h4>
            <Button
              type="button"
              variant="outline"
              className="h-8 px-3 border-emerald-200 text-emerald-900 bg-white hover:bg-emerald-50"
              onClick={() => setShowDetails((v) => !v)}
            >
              {showDetails ? 'Hide detailed results' : 'Show detailed results'}
            </Button>
          </div>

          {chartData.length > 0 ? (
            <div className="rounded-lg border border-emerald-100 bg-white p-3">
              {showDetails ? (
                <>
                  <div className="h-80 w-full overflow-x-auto">
                    <div className="h-full mx-auto" style={{ width: detailedWidthPx, minWidth: detailedWidthPx }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={detailedData}
                          margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
                          barCategoryGap={6}
                          barGap={2}
                        >
                          <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                          <XAxis
                            dataKey="stage"
                            tick={{ fill: '#334155', fontSize: 12 }}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                            height={32}
                          />
                          <YAxis
                            tick={{ fill: '#334155', fontSize: 12 }}
                            tickFormatter={(v) => formatImpactValue(v)}
                            tickLine={false}
                            axisLine={{ stroke: '#e2e8f0' }}
                            width={48}
                          />
                          <Tooltip content={<DetailedTooltip hoveredKey={hoveredKey} />} />
                          {walls.map((wall, i) => {
                            const key = wall.name || wall.id || `wall_${i}`;
                            return (
                              <Bar
                                key={key}
                                dataKey={key}
                                name={key}
                                fill={WALL_COLORS[i % WALL_COLORS.length]}
                                radius={[10, 10, 0, 0]}
                                barSize={detailedBarSize}
                                isAnimationActive
                                animationDuration={500}
                                onMouseEnter={() => setHoveredKey(key)}
                                onMouseLeave={() => setHoveredKey(null)}
                              >
                                <LabelList
                                  dataKey={key}
                                  position="top"
                                  formatter={(value) => (Number.isFinite(value) ? Number(value).toFixed(2) : '—')}
                                  style={{ fill: '#475569', fontSize: 10 }}
                                />
                              </Bar>
                            );
                          })}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
                    {walls.map((w, i) => (
                      <div key={w.id || i} className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded"
                          style={{ background: WALL_COLORS[i % WALL_COLORS.length] }}
                        />
                        <span className="text-emerald-900">{w.name || w.id || `Wall ${i + 1}`}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="h-72 mx-auto" style={{ width: chartWidthPx }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{ top: 8, right: 12, bottom: 8, left: 8 }}
                      barCategoryGap={12}
                      barGap={4}
                      onMouseMove={({ isTooltipActive, activeTooltipIndex }) => {
                        if (isTooltipActive && typeof activeTooltipIndex === 'number') setActiveBarIndex(activeTooltipIndex);
                        else setActiveBarIndex(null);
                      }}
                      onMouseLeave={() => setActiveBarIndex(null)}
                    >
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="4 4" vertical={false} />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: '#334155', fontSize: 12 }}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                        height={32}
                      />
                      <YAxis
                        tick={{ fill: '#334155', fontSize: 12 }}
                        tickFormatter={(v) => formatImpactValue(v)}
                        tickLine={false}
                        axisLine={{ stroke: '#e2e8f0' }}
                        width={48}
                      />
                      <Tooltip content={<ModernTooltip />} />
                      {chartData.length > 0 ? (
                        <ReferenceLine
                          y={Math.min(...chartData.map((d) => Number(d.total) || 0))}
                          stroke="#94a3b8"
                          strokeDasharray="6 6"
                          ifOverflow="extendDomain"
                        />
                      ) : null}
                      <Bar
                        dataKey="total"
                        fill={BAR_COLOR}
                        radius={[10, 10, 0, 0]}
                        barSize={barSizePx}
                        isAnimationActive
                        animationDuration={600}
                        animationEasing="ease-out"
                      >
                        <LabelList
                          dataKey="total"
                          position="top"
                          formatter={(value) => (Number.isFinite(value) ? Number(value).toFixed(2) : '—')}
                          style={{ fill: '#475569', fontSize: 11 }}
                        />
                        {chartData.map((entry, index) => {
                          const isActive = index === activeBarIndex;
                          const fill = isActive ? BAR_ACTIVE : BAR_COLOR;
                          const opacity = isActive ? 1 : 0.9;
                          const stroke = isActive ? '#065f46' : 'none';
                          return <Cell key={`cell-${index}`} fill={fill} opacity={opacity} stroke={stroke} />;
                        })}
                      </Bar>
                      {chartData.length >= 7 ? (
                        <Brush dataKey="name" height={14} stroke="#94a3b8" travellerWidth={8} tickFormatter={(v) => v} />
                      ) : null}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-emerald-200 bg-emerald-50/60 p-4 text-center text-sm text-emerald-900">
              Assign datasets to layers to see results.
            </div>
          )}

          <div className="text-sm text-center text-emerald-900">
            {lowestImpact ? (
              <>
                {Array.isArray(walls) && walls.length > 1 ? (
                  <>
                    <strong>{lowestImpact.wall.name || `Wall ${walls.indexOf(lowestImpact.wall) + 1}`}</strong>{' '}
                    currently has the lowest impact with a total GWP of{' '}
                    <strong>{formatImpactValue(lowestImpact.total)}</strong>{' '}
                    {selectedStages.size > 0 && <>considering stages: {Array.from(selectedStages).join(', ')}.</>}
                  </>
                ) : (
                  <>
                    {(lowestImpact.wall.name || 'This wall')} has a total GWP of{' '}
                    <strong>{formatImpactValue(lowestImpact.total)}</strong>{' '}
                    {selectedStages.size > 0 && <>considering stages: {Array.from(selectedStages).join(', ')}.</>}
                  </>
                )}
              </>
            ) : (
              <span className="text-gray-500">Assign datasets to wall layers to see results.</span>
            )}
          </div>
        </div>
      </section>
    </div>
  );

  if (unstyled) {
    return (
      <div className="space-y-6">
        {!hideHeader && (
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-800">{titleText}</h3>
            <p className="text-sm text-gray-600">{subtitleText}</p>
          </div>
        )}
        {sections}
      </div>
    );
  }

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-emerald-100 border rounded-xl shadow-lg">
      {!hideHeader && (
        <CardHeader>
          <CardTitle className="text-gray-800">{titleText}</CardTitle>
          <CardDescription className="text-sm">{subtitleText}</CardDescription>
        </CardHeader>
      )}
      <CardContent className="space-y-6">{sections}</CardContent>
    </Card>
  );
};

export default Step3Calculations;
